/**
 * Global Execution Store
 *
 * Zustand store tracking HITL requests and running mission states
 * across all subscribed missions. Used by the sidebar to show
 * the "Open" section and running indicators.
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { questionCountdown, type ChoiceQuestion } from '../utils/questionOptions'

export interface GlobalHITLRequest {
  requestId: string
  missionId: string
  missionName: string
  executionId?: string
  nodeId: string
  agentId: string
  prompt: string
  options?: string[]
  modality: string
  /** A structured question ends at its expires_at (agion/Agion#3559). */
  question?: ChoiceQuestion
  timeoutMs?: number
  receivedAt: number
}

/** How long a question has left, by its own countdown. */
function questionMsLeft(request: GlobalHITLRequest): number {
  const { from, lastsMs } = questionCountdown(request)
  return lastsMs ? from + lastsMs - Date.now() : Infinity
}

export interface GlobalGovernanceAlert {
  id: string
  missionId: string
  missionName: string
  nodeId: string
  action: 'deny' | 'escalate'
  reason?: string
  ruleName?: string
  trustTier?: string
  receivedAt: number
}

export type AttentionItem =
  | (GlobalHITLRequest & { itemType: 'hitl' })
  | (GlobalGovernanceAlert & { itemType: 'governance' })

export interface MissionExecutionState {
  /**
   * Derived: true when at least one running execution is tracked for this
   * mission. F1 (chat-ui catch-up): the badge MUST refcount executions, not
   * track a boolean — a mission with two concurrent executions stays "running"
   * until both end. See plans/architecture/2026-05-24-chat-ui-fix-plan.md.
   */
  isRunning: boolean
  hasActiveHITL: boolean
  hasGovernanceAlert: boolean
  /**
   * Internal refcount of running execution IDs. Exposed so debug UIs can read
   * it; callers MUST mutate via addRunningExecution / removeRunningExecution.
   */
  runningExecutions: Set<string>
}

interface ExecutionStore {
  hitlRequests: Map<string, GlobalHITLRequest>
  governanceAlerts: Map<string, GlobalGovernanceAlert>
  missionStates: Map<string, MissionExecutionState>

  addHITLRequest: (req: GlobalHITLRequest) => void
  removeHITLRequest: (requestId: string) => void
  removeHITLRequestsForMission: (missionId: string) => void
  addGovernanceAlert: (alert: GlobalGovernanceAlert) => void
  removeGovernanceAlert: (id: string) => void
  removeGovernanceAlertsForMission: (missionId: string) => void

  /**
   * Track a newly-started execution. Adds executionId to the mission's set
   * and turns the running badge on if it wasn't already.
   */
  addRunningExecution: (missionId: string, executionId: string) => void
  /**
   * Drop a finished execution. Turns the running badge off only when the
   * set becomes empty (so concurrent executions don't prematurely clear it).
   */
  removeRunningExecution: (missionId: string, executionId: string) => void

  /**
   * @deprecated Use addRunningExecution / removeRunningExecution. This
   * boolean-flag API will be removed once all callers (sidebar
   * subscriptions, mission chat) migrate to per-execution tracking.
   */
  setMissionRunning: (missionId: string, isRunning: boolean) => void

  clearMission: (missionId: string) => void
  getActiveHITLsSortedByUrgency: () => GlobalHITLRequest[]
  getAttentionItemsSortedByUrgency: () => AttentionItem[]
}

/**
 * Build (or copy and mutate) a mission state record with sensible defaults.
 * Always returns a new Set for runningExecutions so the store-level Map.set
 * call produces a referentially fresh value (Zustand selectors notice).
 */
function patchState(
  existing: MissionExecutionState | undefined,
  patch: Partial<MissionExecutionState>,
): MissionExecutionState {
  return {
    isRunning: patch.isRunning ?? existing?.isRunning ?? false,
    hasActiveHITL: patch.hasActiveHITL ?? existing?.hasActiveHITL ?? false,
    hasGovernanceAlert: patch.hasGovernanceAlert ?? existing?.hasGovernanceAlert ?? false,
    runningExecutions: patch.runningExecutions ?? new Set(existing?.runningExecutions ?? []),
  }
}

export const useExecutionStore = create<ExecutionStore>()(
  subscribeWithSelector((set, get) => ({
    hitlRequests: new Map(),
    governanceAlerts: new Map(),
    missionStates: new Map(),

    addHITLRequest: (req) =>
      set((state) => {
        const next = new Map(state.hitlRequests)
        // A question held already keeps its first arrival time: the relay
        // replays pending questions on each subscribe (agion/Agion#2327),
        // and the needs-input list orders by that time.
        const held = state.hitlRequests.get(req.requestId)
        next.set(req.requestId, held ? { ...req, receivedAt: held.receivedAt } : req)
        // Also mark mission as having active HITL
        const missionStates = new Map(state.missionStates)
        const existing = missionStates.get(req.missionId)
        missionStates.set(
          req.missionId,
          patchState(existing, {
            hasActiveHITL: true,
            // A HITL request implies the mission is running. Track its
            // execution if we know it so the refcount stays consistent.
            isRunning: true,
          }),
        )
        if (req.executionId) {
          const ms = missionStates.get(req.missionId)!
          ms.runningExecutions.add(req.executionId)
        }
        return { hitlRequests: next, missionStates }
      }),

    removeHITLRequest: (requestId) =>
      set((state) => {
        const prev = state.hitlRequests.get(requestId)
        if (!prev) return state

        const next = new Map(state.hitlRequests)
        next.delete(requestId)

        // Check if mission still has active HITL
        const missionStates = new Map(state.missionStates)
        const missionId = prev.missionId
        const stillHasHITL = Array.from(next.values()).some(
          (r) => r.missionId === missionId,
        )
        const existing = missionStates.get(missionId)
        if (existing) {
          missionStates.set(missionId, patchState(existing, { hasActiveHITL: stillHasHITL }))
        }

        return { hitlRequests: next, missionStates }
      }),

    removeHITLRequestsForMission: (missionId) =>
      set((state) => {
        const next = new Map(state.hitlRequests)
        for (const [key, req] of next) {
          if (req.missionId === missionId) {
            next.delete(key)
          }
        }
        const missionStates = new Map(state.missionStates)
        const existing = missionStates.get(missionId)
        if (existing) {
          missionStates.set(missionId, patchState(existing, { hasActiveHITL: false }))
        }
        return { hitlRequests: next, missionStates }
      }),

    addRunningExecution: (missionId, executionId) =>
      set((state) => {
        const missionStates = new Map(state.missionStates)
        const existing = missionStates.get(missionId)
        const nextSet = new Set(existing?.runningExecutions ?? [])
        nextSet.add(executionId)
        missionStates.set(
          missionId,
          patchState(existing, {
            runningExecutions: nextSet,
            isRunning: true,
          }),
        )
        return { missionStates }
      }),

    removeRunningExecution: (missionId, executionId) =>
      set((state) => {
        const existing = state.missionStates.get(missionId)
        if (!existing) return state
        const nextSet = new Set(existing.runningExecutions)
        nextSet.delete(executionId)
        const missionStates = new Map(state.missionStates)
        missionStates.set(
          missionId,
          patchState(existing, {
            runningExecutions: nextSet,
            isRunning: nextSet.size > 0,
          }),
        )
        return { missionStates }
      }),

    setMissionRunning: (missionId, isRunning) =>
      set((state) => {
        // Deprecated boolean API. We honor it by writing into the refcount
        // set with a sentinel "__legacy_unknown" key so existing callers keep
        // working while the migration to addRunningExecution / removeRunningExecution
        // lands per F1 of the chat-ui catch-up plan.
        const missionStates = new Map(state.missionStates)
        const existing = missionStates.get(missionId)
        const nextSet = new Set(existing?.runningExecutions ?? [])
        if (isRunning) {
          nextSet.add('__legacy_unknown')
        } else {
          // Boolean false clears every execution — that's the pre-F1 behavior.
          nextSet.clear()
        }
        missionStates.set(
          missionId,
          patchState(existing, {
            runningExecutions: nextSet,
            isRunning: nextSet.size > 0,
          }),
        )
        return { missionStates }
      }),

    addGovernanceAlert: (alert) =>
      set((state) => {
        const next = new Map(state.governanceAlerts)
        next.set(alert.id, alert)
        const missionStates = new Map(state.missionStates)
        const existing = missionStates.get(alert.missionId)
        missionStates.set(
          alert.missionId,
          patchState(existing, { hasGovernanceAlert: true }),
        )
        return { governanceAlerts: next, missionStates }
      }),

    removeGovernanceAlert: (id) =>
      set((state) => {
        const prev = state.governanceAlerts.get(id)
        if (!prev) return state

        const next = new Map(state.governanceAlerts)
        next.delete(id)

        const missionStates = new Map(state.missionStates)
        const missionId = prev.missionId
        const stillHasAlert = Array.from(next.values()).some(
          (a) => a.missionId === missionId,
        )
        const existing = missionStates.get(missionId)
        if (existing) {
          missionStates.set(missionId, patchState(existing, { hasGovernanceAlert: stillHasAlert }))
        }

        return { governanceAlerts: next, missionStates }
      }),

    removeGovernanceAlertsForMission: (missionId) =>
      set((state) => {
        const next = new Map(state.governanceAlerts)
        for (const [key, alert] of next) {
          if (alert.missionId === missionId) {
            next.delete(key)
          }
        }
        const missionStates = new Map(state.missionStates)
        const existing = missionStates.get(missionId)
        if (existing) {
          missionStates.set(missionId, patchState(existing, { hasGovernanceAlert: false }))
        }
        return { governanceAlerts: next, missionStates }
      }),

    clearMission: (missionId) =>
      set((state) => {
        const hitlRequests = new Map(state.hitlRequests)
        for (const [key, req] of hitlRequests) {
          if (req.missionId === missionId) {
            hitlRequests.delete(key)
          }
        }
        const governanceAlerts = new Map(state.governanceAlerts)
        for (const [key, alert] of governanceAlerts) {
          if (alert.missionId === missionId) {
            governanceAlerts.delete(key)
          }
        }
        const missionStates = new Map(state.missionStates)
        missionStates.delete(missionId)
        return { hitlRequests, governanceAlerts, missionStates }
      }),

    getActiveHITLsSortedByUrgency: () => {
      const requests = Array.from(get().hitlRequests.values())
      return requests.sort((a, b) => {
        return questionMsLeft(a) - questionMsLeft(b)
      })
    },

    getAttentionItemsSortedByUrgency: () => {
      const hitls: AttentionItem[] = Array.from(get().hitlRequests.values()).map(
        (r) => ({ ...r, itemType: 'hitl' as const })
      )
      const govs: AttentionItem[] = Array.from(get().governanceAlerts.values()).map(
        (a) => ({ ...a, itemType: 'governance' as const })
      )
      const all = [...hitls, ...govs]
      return all.sort((a, b) => {
        // Governance escalate first (critical)
        const aEscalate = a.itemType === 'governance' && a.action === 'escalate' ? 1 : 0
        const bEscalate = b.itemType === 'governance' && b.action === 'escalate' ? 1 : 0
        if (aEscalate !== bEscalate) return bEscalate - aEscalate

        // HITL with timeout next (by remaining time)
        const aRemaining = a.itemType === 'hitl' ? questionMsLeft(a) : Infinity
        const bRemaining = b.itemType === 'hitl' ? questionMsLeft(b) : Infinity
        if (aRemaining !== bRemaining) return aRemaining - bRemaining

        // Governance deny after HITL without timeout
        const aGov = a.itemType === 'governance' ? 1 : 0
        const bGov = b.itemType === 'governance' ? 1 : 0
        if (aGov !== bGov) return aGov - bGov

        // Finally by receivedAt (newest first)
        return b.receivedAt - a.receivedAt
      })
    },
  }))
)
